import React, { useState, useEffect, useCallback } from 'react';
import { wheelsApi, productsApi } from '../../../services/api';
import api from '../../../services/api';
import { Wheel, Product } from '../../../types';
import toast from 'react-hot-toast';
import Modal from '../../../components/Modal';
import { LoadingState } from '../../../components/LoadingSpinner';
import { useAuth } from '../../../contexts/AuthContext';
import { usePageTitle } from '../../../hooks/usePageTitle';

// Components
import WheelsPageHeader from './components/WheelsPageHeader';
import WheelsList from './components/WheelsList';
import WheelForm from './components/WheelForm';
import ProductManagementContent from './components/ProductManagement';
import DeleteWheelModal from './components/DeleteWheelModal';
import ManageProductsPromptModal from './components/ManageProductsPromptModal';
import ExecuteDrawModal from './components/ExecuteDrawModal';
import AIAssistantModal from './components/AIAssistantModal';

// Hooks
import { useWheelForm } from './hooks/useWheelForm';
import { useWheelProducts } from './hooks/useWheelProducts';

const WheelsPage: React.FC = () => {
  usePageTitle('Sorteios');
  const [wheels, setWheels] = useState<Wheel[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [themes, setThemes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [wheelType, setWheelType] = useState<'instant' | 'scheduled'>('instant');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isManagePromptOpen, setIsManagePromptOpen] = useState(false);
  const [isExecuteDrawModalOpen, setIsExecuteDrawModalOpen] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [editingWheel, setEditingWheel] = useState<Wheel | null>(null);
  const [managingWheelProducts, setManagingWheelProducts] = useState<Wheel | null>(null);
  const [deletingWheel, setDeletingWheel] = useState<Wheel | null>(null);
  const [executingDrawWheel, setExecutingDrawWheel] = useState<Wheel | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedWheel, setSavedWheel] = useState<Wheel | null>(null);

  const { currentCompany, isFreePlan } = useAuth();

  const {
    formData,
    formErrors,
    validateForm,
    handleInputChange,
    resetForm,
    setEditingWheel: setFormEditingWheel
  } = useWheelForm();

  const {
    wheelProducts,
    isLoading: isLoadingWheelProducts,
    loadWheelProducts,
    addProduct,
    removeProduct,
    updateOddsWeight,
    saveProducts,
    resetProducts
  } = useWheelProducts();

  const loadWheels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await wheelsApi.getByType(wheelType, { page: 1, limit: 100 });
      setWheels(response.wheels);
    } catch (error: any) {
      toast.error('Erro ao carregar roda de prêmios');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [wheelType]);

  const loadProducts = useCallback(async () => {
    try {
      const response = await productsApi.getAll({ active: true, page: 1, limit: 100 });
      setProducts(response.products);
    } catch (error: any) {
      console.error('Erro ao carregar produtos:', error);
    }
  }, []);

  const loadThemes = useCallback(async () => {
    try {
      const response = await api.get('/themes');
      setThemes(response.data.themes || []);
    } catch (error: any) {
      console.error('Erro ao carregar temas:', error);
    }
  }, []);
  
  useEffect(() => {
    loadWheels();
    loadProducts();
    loadThemes();
  }, [wheelType, loadWheels, loadProducts, loadThemes]); // Reload when wheel type changes


  const filteredWheels = wheels.filter(
    wheel =>
      wheel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wheel.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    try {
      setIsSubmitting(true);

      // Sanitizar dados: converter undefined para null
      const sanitizedData = {
        ...formData,
        prerequisiteContent: formData.prerequisiteContent || null,
        ...(formData.termsType && {
          termsType: formData.termsType,
          termsContent: formData.termsContent
        })
      };

      let wheel: Wheel;
      if (editingWheel) {
        await wheelsApi.update(editingWheel.id, sanitizedData as any);
        toast.success('Sorteio atualizado com sucesso!');
        wheel = { ...editingWheel, ...sanitizedData } as Wheel;
      } else {
        const response = await wheelsApi.create(sanitizedData as any);
        toast.success('Sorteio criado com sucesso!');
        wheel = response.wheel;
      }

      await loadWheels();
      closeModal();

      // Perguntar se deseja gerenciar produtos
      setSavedWheel(wheel);
      setIsManagePromptOpen(true);
    } catch (error: any) {
      toast.error(error.error || 'Erro ao salvar roda');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingWheel) return;

    try {
      setIsSubmitting(true);
      await wheelsApi.delete(deletingWheel.id);
      toast.success('Sorteio excluído com sucesso!');
      await loadWheels();
      setIsDeleteModalOpen(false);
      setDeletingWheel(null);
    } catch (error: any) {
      toast.error(error.error || 'Erro ao excluir sorteio');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicate = async (wheel: Wheel) => {
    try {
      const loadingToast = toast.loading('Duplicando sorteio...');
      const response = await wheelsApi.duplicate(wheel.id);
      toast.dismiss(loadingToast);
      toast.success(`Sorteio "${response.wheel.name}" criado com sucesso!`);
      await loadWheels();
    } catch (error: any) {
      toast.error(error.error || 'Erro ao duplicar sorteio');
    }
  };

  const handleSaveProducts = async () => {
    if (!managingWheelProducts) return;

    setIsSubmitting(true);
    const success = await saveProducts(managingWheelProducts.id);
    setIsSubmitting(false);

    if (success) {
      setIsProductModalOpen(false);
      resetProducts();
    }
  };

  const openCreateModal = () => {
    setEditingWheel(null);
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (wheel: Wheel) => {
    setEditingWheel(wheel);
    setFormEditingWheel(wheel);
    setIsModalOpen(true);
  };

  const openProductModal = async (wheel: Wheel) => {
    setManagingWheelProducts(wheel);
    await loadWheelProducts(wheel.id);
    setIsProductModalOpen(true);
  };

  const openDeleteModal = (wheel: Wheel) => {
    setDeletingWheel(wheel);
    setIsDeleteModalOpen(true);
  };

  const openExecuteDrawModal = (wheel: Wheel) => {
    setExecutingDrawWheel(wheel);
    setIsExecuteDrawModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingWheel(null);
    resetForm();
  };

  const closeProductModal = () => {
    setIsProductModalOpen(false);
    setManagingWheelProducts(null);
    resetProducts();
  };

  const closeExecuteDrawModal = () => {
    setIsExecuteDrawModalOpen(false);
    setExecutingDrawWheel(null);
  };

  const handleExecuteDrawSuccess = () => {
    loadWheels();
  };

  const handleManageProductsPromptConfirm = () => {
    setIsManagePromptOpen(false);
    if (savedWheel) {
      openProductModal(savedWheel);
      setSavedWheel(null);
    }
  };

  const handleManageProductsPromptSkip = () => {
    setIsManagePromptOpen(false);
    setSavedWheel(null);
  };

  const copyWheelUrl = (slug: string) => {
    const url = `http://participar.rodadepremios.com.br/w/${currentCompany?.slug}/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success('Link copiado!');
    });
  };

  const generateQRCode = (slug: string) => {
    const url = `http://participar.rodadepremios.com.br/w/${currentCompany?.slug}/${slug}`;
    window.open(
      `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(url)}`,
      '_blank'
    );
  };

  if (loading) {
    return <LoadingState message="Carregando roda de prêmios..." />;
  }

  return (
    <div className="space-y-6">
      <WheelsPageHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onCreateClick={openCreateModal}
        onAIClick={() => setIsAIModalOpen(true)}
        wheelType={wheelType}
        onTypeChange={setWheelType}
      />

      <WheelsList
        wheels={filteredWheels}
        wheelType={wheelType}
        isLoadingWheelProducts={isLoadingWheelProducts}
        companySlug={currentCompany?.slug}
        onCopyUrl={copyWheelUrl}
        onGenerateQR={generateQRCode}
        onManageProducts={openProductModal}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
        onDuplicate={handleDuplicate}
        onExecuteDraw={openExecuteDrawModal}
      />

      {/* Wheel Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingWheel ? 'Editar Sorteio' : 'Novo Sorteio'}
        size="lg"
      >
        <WheelForm
          formData={formData}
          formErrors={formErrors}
          isEditing={!!editingWheel}
          isSubmitting={isSubmitting}
          isFreePlan={isFreePlan}
          themes={themes}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          onCancel={closeModal}
        />
      </Modal>

      {/* Manage Products Modal */}
      <Modal
        isOpen={isProductModalOpen}
        onClose={closeProductModal}
        title={`Gerenciar Produtos - ${managingWheelProducts?.name}`}
        size="xl"
      >
        <ProductManagementContent
          products={products}
          wheelProducts={wheelProducts}
          isSubmitting={isSubmitting}
          onAddProduct={addProduct}
          onUpdateOddsWeight={updateOddsWeight}
          onRemoveProduct={removeProduct}
          onSave={handleSaveProducts}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteWheelModal
        isOpen={isDeleteModalOpen}
        wheel={deletingWheel}
        isSubmitting={isSubmitting}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
      />

      {/* Execute Draw Modal */}
      <ExecuteDrawModal
        isOpen={isExecuteDrawModalOpen}
        wheel={executingDrawWheel}
        onClose={closeExecuteDrawModal}
        onSuccess={handleExecuteDrawSuccess}
      />

      {/* Manage Products Prompt Modal */}
      <ManageProductsPromptModal
        isOpen={isManagePromptOpen}
        wheelName={savedWheel?.name || ''}
        onConfirm={handleManageProductsPromptConfirm}
        onSkip={handleManageProductsPromptSkip}
      />

      {/* AI Assistant Modal */}
      <AIAssistantModal
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        onSuccess={() => {
          loadWheels();
          loadProducts();
        }}
      />
    </div>
  );
};

export default WheelsPage;
